import { z } from 'zod';
import { metricsTypeEnum } from '@features/ai-insights';

const cpgDimensionSchema = z.object({
  id: z.number(),
  cpgReportId: z.number(),
  dimensionName: z.string(),
  dimensionValue: z.string(),
  impressions: z.number(),
  uniqueUsers: z.number(),
  matchedForAttribution: z.number(),
  incrementalSalesLift: z.number().nullable(),
  incrementalSalesLiftConfidenceLevel: z.number().nullable(),
  projectedIncrementalSales: z.number().nullable(),
  totalProjectedSales: z.number().nullable(),
  salesLiftIndex: z.number().nullable(),
  salesPerImpressionIndex: z.number().nullable(),
  incrementalSalesPerImpressionIndex: z.number().nullable(),
  averagePricePerTransaction: z.number().nullable(),
  averageTotalBasketAmount: z.number().nullable(),
  penetration: z.number().nullable(),
  averagePurchaseFrequency: z.number().nullable(),
  salesRate: z.number().nullable(),
  statisticallySignificant: z.boolean(),
});

const cpgRetailerSchema = z.object({
  id: z.number(),
  cpgReportId: z.number(),
  name: z.string(),
  incrementalSalesLift: z.number().nullable(),
  incrementalSalesLiftConfidenceLevel: z.number().nullable(),
  projectedIncrementalSales: z.number().nullable(),
  totalProjectedSales: z.number().nullable(),
  salesLiftIndex: z.number().nullable(),
  averagePricePerTransaction: z.number().nullable(),
  averageTotalBasketAmount: z.number().nullable(),
  penetration: z.number().nullable(),
  averagePurchaseFrequency: z.number().nullable(),
  salesRate: z.number().nullable(),
  statisticallySignificant: z.boolean(),
  hidden: z.boolean(),
});

const cpgAiInsightSchema = z.object({
  dimensionName: z.string().nullable(),
  publisher: z.string().nullable(),
  summary: z.array(z.string()),
  metricsType: metricsTypeEnum,
});

const cpgReportSchema = z.object({
  id: z.number(),
  campaignId: z.number(),
  status: z.enum(['pending', 'approved']),
  reportDate: z.string(),
  impressions: z.number(),
  uniqueUsers: z.number(),
  matchedForAttribution: z.number(),
  retailerBanners: z.number(),
  incrementalSalesLift: z.number().nullable(),
  incrementalSalesRate: z.number().nullable(),
  incrementalSalesLiftConfidenceLevel: z.number().nullable(),
  projectedIncrementalSales: z.number().nullable(),
  totalProjectedSales: z.number().nullable(),
  totalSalesRate: z.number().nullable(),
  averagePricePerTransaction: z.number().nullable(),
  averageTotalBasketAmount: z.number().nullable(),
  penetration: z.number().nullable(),
  averagePurchaseFrequency: z.number().nullable(),
  statisticallySignificant: z.boolean(),
  dimensions: z.array(cpgDimensionSchema),
  retailers: z.array(cpgRetailerSchema),
  aiInsights: z.array(cpgAiInsightSchema),
  postExposedPurchases: z.number().nullable(),
  penetrationControl: z.number().nullable(),
  averagePricePerTransactionControl: z.number().nullable(),
  averagePurchaseFrequencyControl: z.number().nullable(),
  penetrationLift: z.number().nullable(),
  averagePricePerTransactionLift: z.number().nullable(),
  averagePurchaseFrequencyLift: z.number().nullable(),
  existingBuyerPercentage: z.number().nullable(),
  newBuyerPercentage: z.number().nullable(),
  existingBuyerSales: z.number().nullable(),
  newBuyerSales: z.number().nullable(),
});

export const cpgReportsResponse = z.object({
  data: z.object({
    reports: z.array(cpgReportSchema),
  }),
});

export type CpgReportsResponse = z.infer<typeof cpgReportsResponse>;
export type CpgReport = z.infer<typeof cpgReportSchema>;
export type CpgRetailer = z.infer<typeof cpgRetailerSchema>;
export type CpgDimension = z.infer<typeof cpgDimensionSchema>;
